import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wordmark } from "../components/site/Wordmark";
import { Input } from "../components/ui/input";
import { Lock, Inbox, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (adminKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setRows(res.data);
      setAuthed(true);
    } catch (e) {
      toast.error("Invalid admin key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const login = (e) => {
    e.preventDefault();
    if (!key) return toast.error("Enter the admin key.");
    load(key);
  };

  return (
    <div className="grain min-h-screen bg-[#050505] text-[#f5f5f0]">
      <header className="border-b border-[#d4af37]/12">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 h-20 flex items-center justify-between">
          <Wordmark size="text-xl" />
          <span className="text-xs tracking-luxe uppercase text-[#9a968c]">
            Enquiries Dashboard
          </span>
        </div>
      </header>

      {!authed ? (
        <div className="mx-auto max-w-md px-6 py-28">
          <div className="gold-border glass p-8">
            <span className="w-12 h-12 rounded-full border border-[#d4af37]/40 flex items-center justify-center mb-6">
              <Lock size={18} className="text-[#d4af37]" />
            </span>
            <h1 className="font-serif-lux text-3xl">Restricted Access</h1>
            <p className="mt-2 text-sm text-[#9a968c]">
              Enter the admin key to view lead enquiries.
            </p>
            <form onSubmit={login} className="mt-6 space-y-4">
              <Input
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                className="bg-black/30 border-[#d4af37]/20 text-[#f5f5f0] focus-visible:ring-[#d4af37] h-12"
              />
              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="btn-gold w-full rounded-full py-3.5 text-xs tracking-[0.18em] uppercase font-semibold disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Enter Dashboard"}
              </button>
            </form>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-14">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <Inbox size={20} className="text-[#d4af37]" />
              <h1 className="font-serif-lux text-3xl">
                {rows.length} Enquir{rows.length === 1 ? "y" : "ies"}
              </h1>
            </div>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(key)}
              className="btn-ghost rounded-full px-5 py-2.5 text-xs tracking-[0.16em] uppercase flex items-center gap-2"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          <div className="gold-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="bg-[#0b0b0b] text-left text-[11px] tracking-[0.14em] uppercase text-[#d4af37]">
                    <th className="px-5 py-4 font-medium">Name</th>
                    <th className="px-5 py-4 font-medium">Contact</th>
                    <th className="px-5 py-4 font-medium">Residence</th>
                    <th className="px-5 py-4 font-medium">Message</th>
                    <th className="px-5 py-4 font-medium">Source</th>
                    <th className="px-5 py-4 font-medium">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-16 text-center text-[#6b6b64]">
                        No enquiries yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-[#d4af37]/10 hover:bg-white/[0.02]"
                      >
                        <td className="px-5 py-4 text-[#f5f5f0]">{r.name}</td>
                        <td className="px-5 py-4 text-[#9a968c]">
                          <div>{r.email}</div>
                          <div>{r.phone}</div>
                        </td>
                        <td className="px-5 py-4 text-[#c9c5ba]">
                          {r.configuration || "—"}
                        </td>
                        <td className="px-5 py-4 text-[#9a968c] max-w-xs">
                          {r.message || "—"}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${r.source === "chatbot" ? "border-[#d4af37]/50 text-[#d4af37]" : "border-[#3a3a3a] text-[#9a968c]"}`}>
                            {r.source === "chatbot" ? "Concierge" : "Website"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-[#6b6b64] whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
